import { Core, Decorative } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web/common/localization";
import { CheckInCircle } from "@faire/web/slate/icons/foundation/CheckInCircle";
import { Column, Flex, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

export const CalendlySection: React.FC = () => {
  const viewport = useViewport();

  return (
    <Wrapper align="center">
      <Spacer size="8x" />
      {viewport.isTabletAndAbove ? (
        <CollapsibleRow justify="center">
          <Column>
            <EducationalTextSection />
            <Spacer size="5x" />
            <EducationalCheckPoints />
            <Spacer size="5x" />
          </Column>
        </CollapsibleRow>
      ) : (
        <Column>
          <EducationalTextSection />
          <Spacer size="5x" />
          <EducationalCheckPoints />
          <Spacer size="5x" />
        </Column>
      )}
      <Spacer size="8x" />
    </Wrapper>
  );
};

const EducationalTextSection: React.FC = () => {
  return (
    <EducationalTextWrapper>
      <Typography
        variant={{
          mobileAndAbove: "displaySSerifRegular",
          tabletAndAbove: "displayMSerifRegular",
        }}
        align={{
          mobileAndAbove: "left",
          tabletAndAbove: "center",
        }}
      >
        <StrictLocalMsg
          defaultMessage="Get one-on-one help from the Faire team to launch your shop"
          description={{
            text: "The brands can get personal assistance from partners at Faire to get started and set up their shop on Faire.",
          }}
        />
      </Typography>
      <Spacer size="3x" />
      <Typography
        variant="subheadingSansRegular"
        align={{
          mobileAndAbove: "left",
          tabletAndAbove: "center",
        }}
      >
        <StrictLocalMsg
          defaultMessage="Your brand partner can provide detailed info and analytics that show what to expect from Faire—and then help you set up your shop."
          description={{
            text: "Personal assistance from people at Faire will give you detailed information and statistics about what running a shop on Faire looks like.",
          }}
        />
      </Typography>
    </EducationalTextWrapper>
  );
};

const EducationalCheckPoints: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  return (
    <CheckPointWrapper>
      <CheckPointItem
        text={strictLocalize({
          defaultMessage: "Discover your sales estimates and growth potential",
          description: {
            text: "Highlights of the value to schedule a call with the sales team",
          },
        })}
      />
      <Spacer size="2x" />
      <CheckPointItem
        text={strictLocalize({
          defaultMessage: "Learn how Faire will integrate with your workflow",
          description: {
            text: "Highlights of the value to schedule a call with the sales team",
          },
        })}
      />
      <Spacer size="2x" />
      <CheckPointItem
        text={strictLocalize({
          defaultMessage: "Receive 1-on-1 support to set up your shop",
          description: {
            text: "Highlights of the value to schedule a call with the sales team",
          },
        })}
      />
    </CheckPointWrapper>
  );
};

const CheckPointItem: React.FC<{
  text: string;
}> = ({ text }) => {
  return (
    <Row align="center">
      <CheckInCircle fontSize="24px" color={Decorative.icon.green} />
      <Spacer size={20} />
      <Typography variant="subheadingSansMedium">{text}</Typography>
    </Row>
  );
};

const Wrapper = styled(Column)`
  width: 100%;
  background-color: ${Decorative.tint.orange};
  padding: ${getSpacing(0, "7x")};
  border-bottom: solid 1px ${Core.border.muted};

  ${responsiveMediaQueries.xLargeAndAbove} {
    padding: ${getSpacing(0, 172)};
  }

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing(0, "4x")};
  }
`;

// eslint-disable-next-line @faire/prefer-slate-attrs -- move direction to props
const CollapsibleRow = styled(Flex)`
  width: 100%;
  height: 100%;

  flex-direction: row;
  align-items: flex-start;
  @media (max-width: 1280px) {
    flex-direction: column;
    align-items: center;
  }
`;

const EducationalTextWrapper = styled(Column)`
  width: 600px;

  ${responsiveMediaQueries.xLargeAndAbove} {
    width: 768px;
  }

  ${responsiveMediaQueries.mobile} {
    width: 100%;
    min-width: 0;
  }
`;

const CheckPointWrapper = styled(Column)`
  width: 500px;
  align-self: center;

  ${responsiveMediaQueries.mobile} {
    width: 100%;
    min-width: 0;
  }
`;
